import React, { useState, useEffect } from 'react';
import { createReferral } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { formatInternationalPhoneNumber, validatePhoneNumber } from '../utils/phoneUtils';

const ReferEarnModal = ({ isOpen, onClose, jobTitle, job, onReferralSuccess }) => {
    const { showSuccess, showError } = useToast();
    const [candidates, setCandidates] = useState([
        { id: 1, name: '', mobile: '' }
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handle body overflow when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        
        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    // Debug props when modal opens
    if (isOpen) {
        console.log('[ReferEarnModal] Modal opened with props:');
        console.log('[ReferEarnModal] jobTitle:', jobTitle);
        console.log('[ReferEarnModal] job object:', job);
        console.log('[ReferEarnModal] job?.jobName:', job?.jobName);
        console.log('[ReferEarnModal] job?.title:', job?.title);
    }
    
    if (!isOpen) {
        return null;
    }

    const addCandidate = () => {
        const newId = Math.max(...candidates.map(c => c.id)) + 1;
        setCandidates([...candidates, { id: newId, name: '', mobile: '' }]);
    };

    const removeCandidate = (id) => {
        if (candidates.length > 1) {
            const updatedCandidates = candidates.filter(c => c.id !== id);
            // Re-number candidates after removal
            const renumberedCandidates = updatedCandidates.map((candidate, index) => ({
                ...candidate,
                id: index + 1
            }));
            setCandidates(renumberedCandidates);
        }
    };

    const updateCandidate = (id, field, value) => {
        setCandidates(candidates.map(c => 
            c.id === id ? { ...c, [field]: value } : c
        ));
    };

    const handleClose = () => {
        // Reset form like reference implementation
        setCandidates([{ id: 1, name: '', mobile: '' }]);
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Collect all candidates with valid data
        const validCandidates = [];
        candidates.forEach(candidate => {
            const name = candidate.name?.trim();
            const mobile = candidate.mobile?.trim();
            
            if (name && mobile) {
                // Validate name: letters and spaces only, 2-50 chars
                const nameRegex = /^[A-Za-z ]{2,50}$/;
                if (!nameRegex.test(name)) {
                    showError('Full Name should contain letters and spaces only (2-50 characters).');
                    return;
                }
                // Format and validate mobile number (supports international numbers)
                const cleanMobile = formatInternationalPhoneNumber(mobile);
                const validation = validatePhoneNumber(cleanMobile);
                if (validation.isValid) {
                    validCandidates.push({ name, mobile: cleanMobile });
                } else {
                    showError(`Invalid mobile number for ${name}. ${validation.errorMessage}`);
                    return;
                }
            }
        });
        
        if (validCandidates.length === 0) {
            showError('Please add at least one candidate with valid details.');
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Create referral using the API with position ID
            // Check multiple possible field names for position ID
            const positionId = job?.positionId || job?.position_id || job?.id || job?.jobId || job?.job_id || null;
            
            const referralData = {
                jobId: positionId, // Pass position ID (null will be handled as direct referral)
                candidates: validCandidates
            };
            
            if (positionId) {
                const fieldUsed = job?.positionId ? 'positionId' : 
                                 job?.position_id ? 'position_id' : 
                                 job?.id ? 'id' : 
                                 job?.jobId ? 'jobId' : 
                                 job?.job_id ? 'job_id' : 'unknown';
                console.log(`[ReferEarnModal] Using job position ID: ${positionId} (from field: ${fieldUsed})`);
            } else {
                console.log('[ReferEarnModal] No position ID found, API will use 0 for direct referral');
            }
            
            console.log('[ReferEarnModal] Submitting referral with position ID:', positionId);
            console.log('[ReferEarnModal] Full referral data:', referralData);
            console.log('[ReferEarnModal] Job object:', job);
            console.log('[ReferEarnModal] Job object keys:', Object.keys(job || {}));
            console.log('[ReferEarnModal] job.positionId:', job?.positionId);
            console.log('[ReferEarnModal] job.id:', job?.id);
            console.log('[ReferEarnModal] job.jobId:', job?.jobId);
            console.log('[ReferEarnModal] All job fields:', JSON.stringify(job, null, 2));
            
            const result = await createReferral(referralData);
            
            console.log('[ReferEarnModal] Referral result:', result);
            console.log('[ReferEarnModal] Result success:', result.success);
            console.log('[ReferEarnModal] Result message:', result.message);
            console.log('[ReferEarnModal] Result data:', result.data);
            
            if (result.success) {
                const apiMsg = (result && typeof result.message === 'string') ? result.message.trim() : '';

                // Only show green success when the API explicitly says so
                if (apiMsg === 'Referrals added successfully') {
                    showSuccess(apiMsg);
                    if (onReferralSuccess) {
                        onReferralSuccess();
                    }
                    handleClose();
                } else {
                    showError(apiMsg || 'No new candidates were added');
                }
            } else {
                // Handle different response formats
                let errorMessage = 'Failed to add candidate. Please try again.';
                
                if (result.message && result.message !== 'Unknown response') {
                    errorMessage = result.message;
                } else if (result.data && result.data.message) {
                    errorMessage = result.data.message;
                }
                
                // Close modal for position-related errors (position closed, no vacancies, etc.)
                if (result.positionUnavailable) {
                    handleClose();
                    if (onReferralSuccess) {
                        onReferralSuccess(); // This closes the parent JobDetailsModal too
                    }
                }
                
                showError(errorMessage);
            }
            
        } catch (error) {
            console.error('[ReferEarnModal] Error creating referral:', error);
            showError('An error occurred while submitting the referral. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    return (
        <div 
            style={{
                display: 'flex',
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(0, 0, 0, 0.5)', // Normal modal overlay
                zIndex: 10000, // Higher than JobDetailsModal
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={handleOverlayClick}
        >
            <div style={{
                background: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '75vh',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
            }}>
                {/* Fixed Modal Header */}
                <div style={{
                    position: 'sticky',
                    top: 0,
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    color: 'white',
                    padding: '24px',
                    borderRadius: '12px 12px 0 0',
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <h2 style={{
                                margin: '0 0 0.5rem 0',
                                fontSize: '1.5rem',
                                fontWeight: '700'
                            }}>
                                Refer & Earn
                            </h2>
                            <p style={{
                                margin: '0',
                                opacity: '0.9',
                                fontSize: '0.875rem'
                            }}>
                                Refer candidates for: {jobTitle || job?.jobName || job?.title || 'this position'}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            aria-label="Close"
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'white',
                                fontSize: '22px',
                                cursor: 'pointer',
                                padding: 0,
                                borderRadius: '50%',
                                width: '44px',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.2s, box-shadow 0.2s'
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
                            onFocus={(e) => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.5), 0 0 0 6px rgba(37, 99, 235, 0.5)';
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                {/* Modal Content with Fixed Layout */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(75vh - 80px)',
                    overflow: 'hidden'
                }}>
                    <form onSubmit={handleSubmit} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}>
                        {/* Scrollable Content Area */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '1.5rem'
                        }}>
                            {/* Earning Info */}
                            <div style={{
                                background: '#eff6ff',
                                border: '1px solid #dbeafe',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                marginBottom: '1.5rem'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    marginBottom: '1.5rem'
                                }}>
                                    <i className="fas fa-gift" style={{
                                        color: '#2563eb',
                                        fontSize: '1.25rem'
                                    }}></i>
                                    <h3 style={{
                                        margin: '0',
                                        color: '#2563eb',
                                        fontSize: '1.125rem',
                                        fontWeight: '600'
                                    }}>
                                        Earn ₹500 per successful referral!
                                    </h3>
                                </div>
                                <p style={{
                                    margin: '0',
                                    color: '#1e40af',
                                    fontSize: '0.875rem',
                                    lineHeight: '1.5'
                                }}>
                                    Refer qualified candidates and earn ₹500 when they join and complete one month with us.
                                </p>
                            </div>

                            {/* Candidate Forms */}
                            <div>
                                {candidates.map((candidate, index) => (
                                    <div
                                        key={candidate.id}
                                        style={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '12px',
                                            padding: '1rem 1.5rem',
                                            marginBottom: '1.5rem',
                                            position: 'relative'
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '1rem'
                                        }}>
                                            <h4 style={{
                                                margin: '0',
                                                color: '#1f2937',
                                                fontSize: '1rem',
                                                fontWeight: '600'
                                            }}>
                                                Candidate {index + 1}
                                            </h4>
                                            {candidates.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeCandidate(candidate.id)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#ef4444',
                                                        cursor: 'pointer',
                                                        padding: '0.25rem',
                                                        borderRadius: '4px',
                                                        transition: 'background 0.2s',
                                                        fontSize: '0.875rem'
                                                    }}
                                                    title="Remove candidate"
                                                    onMouseOver={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.1)'}
                                                    onMouseOut={(e) => e.target.style.background = 'none'}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            )}
                                        </div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '1.5rem'
                                        }}>
                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    fontWeight: '500',
                                                    color: '#374151',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    Full Name*
                                                </label>
                                                <input
                                                    type="text"
                                                    value={candidate.name}
                                                    onChange={(e) => {
                                                        const cleaned = e.target.value
                                                            .replace(/[^A-Za-z ]/g, '')
                                                            .replace(/\s{2,}/g, ' ');
                                                        updateCandidate(candidate.id, 'name', cleaned);
                                                    }}
                                                    required
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '12px',
                                                        fontSize: '0.875rem',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    placeholder="Enter candidate's full name"
                                                    maxLength="50"
                                                    pattern="[A-Za-z ]{2,50}"
                                                    title="Use letters and spaces only (2-50 characters)"
                                                    autoComplete="off"
                                                    onInput={(e) => {
                                                        const cleaned = e.target.value
                                                            .replace(/[^A-Za-z ]/g, '')
                                                            .replace(/\s{2,}/g, ' ');
                                                        if (e.target.value !== cleaned) {
                                                            e.target.value = cleaned;
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    fontWeight: '500',
                                                    color: '#374151',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    Mobile Number*
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={candidate.mobile}
                                                    onChange={(e) => {
                                                        // Format international phone numbers automatically
                                                        const formatted = formatInternationalPhoneNumber(e.target.value);
                                                        updateCandidate(candidate.id, 'mobile', formatted);
                                                    }}
                                                    required
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '12px',
                                                        fontSize: '0.875rem',
                                                        boxSizing: 'border-box'
                                                    }}
                                                    placeholder="Enter candidate phone number"
                                                    maxLength="15"
                                                    title="Enter candidate phone number"
                                                    inputMode="numeric"
                                                    autoComplete="tel"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Add More Candidate Button */}
                            <button
                                type="button"
                                onClick={addCandidate}
                                style={{
                                    background: '#f8fafc',
                                    color: '#2563eb',
                                    border: '2px dashed #2563eb',
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.3s ease',
                                    width: '100%',
                                    justifyContent: 'center'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#eff6ff'}
                                onMouseOut={(e) => e.target.style.background = '#f8fafc'}
                            >
                                <i className="fas fa-plus"></i>
                                Add Another Candidate
                            </button>
                        </div>

                        {/* Fixed Submit Section with Separator */}
                        <div style={{
                            borderTop: '1px solid #e5e7eb',
                            padding: '24px 24px 24px 24px',
                            backgroundColor: '#ffffff',
                            flexShrink: 0,
                            display: 'flex',
                            gap: '1rem',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                    background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.75rem 2rem',
                                    borderRadius: '12px',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSubmitting) {
                                        e.target.style.background = 'linear-gradient(135deg, #1d4ed8, #2563eb)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSubmitting) {
                                        e.target.style.background = 'linear-gradient(135deg, #2563eb, #3b82f6)';
                                    }
                                }}
                            >
                                <i className={`fas ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                                {isSubmitting ? 'Submitting...' : 'Submit Referrals'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReferEarnModal;
